/**
 * SequencingCommissioningPage — iter150AG Phase 1D operator wizard.
 *
 * Vertical 15-step stepper that walks the operator from "idle" all the
 * way to "auto_sequencing". Each step:
 *
 *   1. Marks the current state with a coloured ring + "you are here" chip.
 *   2. Shows the artifact required to leave the state (e.g. "Set Pset",
 *      "Accept disclaimer", "Assign types to N devices").
 *   3. Renders an "Advance" button that POSTs to /fsm/advance, optionally
 *      with a payload (Pset value, disclaimer acceptance).
 *   4. Surfaces every blocking pre-requisite from the server in a list
 *      so the operator knows exactly what's missing.
 *
 * The "Reset" button (top-right) slams the group back to idle. Use this
 * as the operator panic-button.
 *
 * The component is read-mostly: it reloads the FSM summary after every
 * advance, member-type assignment, or one-off action so the UI always
 * reflects server-side state.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Compass, CheckCircle, Circle, ArrowRight,
  ArrowCounterClockwise, Pause, Warning, Lock, FlowArrow, WifiHigh,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Dr404WifiSetupDialog from "@/components/sequencing/Dr404WifiSetupDialog";
import {
  seqGetGroup, seqFsmStatus, seqFsmAdvance, seqFsmReset,
  seqAssignMemberType, seqListMemberTypes, seqListSchedule,
  seqUpsertScheduleSlot, seqStartFillTest, seqCompleteFillTest,
  seqStartLossTest, seqCompleteLossTest, seqListFillTests, seqListLossTests,
  seqListTypes,
} from "@/lib/sequencingApi";


// Human-friendly labels for the wizard. Keep stable ordering.
const STEP_LABELS = {
  idle:                   "Idle",
  detecting:              "Detect compressors",
  parameter_registration: "Register parameters",
  type_verified:          "Verify compressor types",
  pset_set:               "Set common Pset",
  disclaimer1_accepted:   "Accept pre-test disclaimer",
  empty_system_prompt:    "Empty air lines",
  fill_test:              "Run fill test",
  loss_test:              "Run loss test",
  disclaimer2_accepted:   "Accept auto-control disclaimer",
  schedule_set:           "Set schedule",
  ready_to_start:         "Ready to start",
  auto_sequencing:        "Auto sequencing",
};

const STEP_BLURB = {
  idle:                   "Group is freshly created and not yet under commissioning.",
  detecting:              "Operator confirms the system can talk to the compressors in this group.",
  parameter_registration: "At least one member must be added before parameter registration is meaningful.",
  type_verified:          "Map every member's serial number to a Compressor Type from the catalog.",
  pset_set:               "Enter the shared target pressure (bar). Must be inside 0.5..25 bar.",
  disclaimer1_accepted:   "Operator accepts the pre-test safety disclaimer.",
  empty_system_prompt:    "Operator confirms compressed-air lines are empty before testing.",
  fill_test:              "At least one compressor fill-time test must be completed (one per member recommended).",
  loss_test:              "Run a 10-minute leak/loss test with all compressors off.",
  disclaimer2_accepted:   "Operator accepts the auto-control disclaimer.",
  schedule_set:           "Optional: configure weekday STOP windows when the group must shed (e.g. lunch break, night-shift quiet hours). Default is run 24/7.",
  ready_to_start:         "Final review of every pre-requisite before automatic sequencing begins.",
  auto_sequencing:        "Engine issues real start/stop commands to the Control Plane until paused.",
};


export default function SequencingCommissioningPage() {
  const { gid } = useParams();
  const [group, setGroup] = useState(null);
  const [fsm,   setFsm]   = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState("");

  const [members, setMembers]           = useState([]);
  const [memberTypes, setMemberTypes]   = useState({});
  const [typeCatalog, setTypeCatalog]   = useState([]);

  const [schedSlots, setSchedSlots]     = useState([]);
  const [fillTests,  setFillTests]      = useState([]);
  const [lossTests,  setLossTests]      = useState([]);

  const [psetInput, setPsetInput] = useState("");

  async function refresh() {
    try {
      const [g, f, mt, sched, ft, lt, tc] = await Promise.all([
        seqGetGroup(gid),
        seqFsmStatus(gid),
        seqListMemberTypes(gid).catch(() => ({ items: [] })),
        seqListSchedule(gid).catch(() => ({ items: [] })),
        seqListFillTests(gid).catch(() => ({ items: [] })),
        seqListLossTests(gid).catch(() => ({ items: [] })),
        seqListTypes().catch(() => ({ items: [] })),
      ]);
      setGroup(g);
      setFsm(f);
      setMembers(g.members || []);
      setMemberTypes(Object.fromEntries((mt.items || []).map(r => [r.device_id, r.type_code])));
      setSchedSlots(sched.items || []);
      setFillTests(ft.items || []);
      setLossTests(lt.items || []);
      setTypeCatalog(tc.items || []);
      setPsetInput(String(g.common_pset ?? ""));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [gid]);

  async function advance(target, payload = {}) {
    setBusy(true);
    try {
      const res = await seqFsmAdvance(gid, target, payload);
      if (res?.ok) {
        toast.success(`Advanced to ${target}`);
      }
      await refresh();
    } catch (e) {
      const detail = e?.response?.data;
      if (detail?.required?.length) {
        toast.error("Blocked: " + detail.required.slice(0, 2).join("; "));
      } else {
        toast.error(detail?.detail || e?.message || "Advance failed");
      }
      await refresh();
    } finally { setBusy(false); }
  }

  async function reset() {
    if (!window.confirm("Reset commissioning back to idle? This disables the engine but keeps members + tests.")) return;
    setBusy(true);
    try {
      await seqFsmReset(gid);
      toast.success("Group reset to idle");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Reset failed");
    } finally { setBusy(false); }
  }

  if (err) return (
    <div className="p-6 text-red-700 text-sm" data-testid="fsm-page-error">{err}</div>
  );
  if (!group || !fsm) return (
    <div className="p-6 text-slate-500 text-sm" data-testid="fsm-page-loading">Loading…</div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sequencing-commissioning-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to={`/sequencing/groups/${gid}`} data-testid="fsm-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <FlowArrow size={22} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            {group.name} — commissioning
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            Step {Math.min(fsm.current_index + 2, fsm.states_forward.length)} of {fsm.states_forward.length} · next action: <b>{fsm.next || "—"}</b>
          </p>
        </div>
        <button
          data-testid="fsm-reset"
          onClick={reset} disabled={busy}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          <ArrowCounterClockwise size={12} /> Reset
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-3">
        {fsm.current === "auto_sequencing" && (
          <div data-testid="fsm-banner-auto"
               className="bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs p-3 rounded-sm">
            <b>Auto sequencing is active.</b> The engine is issuing real start/stop
            commands to the Control Plane on every tick. Pause to stop the
            engine without losing your commissioning state.
          </div>
        )}
        {fsm.current === "paused" && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs p-3 rounded-sm" data-testid="fsm-banner-paused">
            <b>Paused.</b> Engine is OFF. Click "Resume to ready" below to
            re-arm, then advance to <code>auto_sequencing</code>.
          </div>
        )}
        {fsm.current === "fault" && (
          <div className="bg-rose-50 border border-rose-300 text-rose-800 text-xs p-3 rounded-sm" data-testid="fsm-banner-fault">
            <b>Faulted.</b> Engine is OFF. Resolve the fault then click Reset.
          </div>
        )}

        <ol className="space-y-2" data-testid="fsm-steps">
          {fsm.states_forward.map((s, i) => {
            // iter150AG.fix2 — the FSM's `current` field names the *last
            // completed checkpoint*, not the row where the operator is
            // about to act. The row that needs action is the one whose
            // own state matches `fsm.next`. Aligning "YOU ARE HERE" /
            // action rendering with `fsm.next` fixes the off-by-one
            // where e.g. the schedule editor appeared inside the
            // "Accept auto-control disclaimer" card.
            const isCurrent = s === fsm.next;
            const isPast    = i <= fsm.current_index;
            const isNext    = false;
            return (
              <Step key={s}
                    state={s}
                    label={STEP_LABELS[s] || s}
                    blurb={STEP_BLURB[s] || ""}
                    index={i + 1}
                    isCurrent={isCurrent}
                    isPast={isPast}
                    isNext={isNext}
                    fsm={fsm}
                    group={group}
                    members={members}
                    memberTypes={memberTypes}
                    typeCatalog={typeCatalog}
                    schedSlots={schedSlots}
                    fillTests={fillTests}
                    lossTests={lossTests}
                    psetInput={psetInput}
                    setPsetInput={setPsetInput}
                    busy={busy}
                    onAdvance={advance}
                    onAssignType={async (did, code) => {
                      try {
                        await seqAssignMemberType(gid, did, code);
                        const m = members.find(x => x.device_id === did);
                        toast.success(`${m?.serial_number || did} → ${code}`);
                        await refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Type assign failed");
                      }
                    }}
                    onAddSlot={async (weekday) => {
                      try {
                        await seqUpsertScheduleSlot(gid, { weekday, start_time: "08:00",
                                                            end_time: "17:00", run: true });
                        toast.success(`Schedule slot added (weekday ${weekday})`);
                        await refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Slot save failed");
                      }
                    }}
                    onRunFillTest={async (deviceId) => {
                      try {
                        const start = await seqStartFillTest(gid, { device_id: deviceId,
                                                                     start_pressure: 0.0 });
                        await seqCompleteFillTest(start.id, { end_pressure: group.common_pset || 8.0,
                                                              result: "passed" });
                        const m = members.find(x => x.device_id === deviceId);
                        toast.success(`Fill test recorded for ${m?.serial_number || deviceId}`);
                        await refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Fill test failed");
                      }
                    }}
                    onRunLossTest={async () => {
                      try {
                        const start = await seqStartLossTest(gid, { start_pressure: group.common_pset || 8.0 });
                        await seqCompleteLossTest(start.id, { end_pressure: (group.common_pset || 8.0) - 0.05,
                                                              result: "completed" });
                        toast.success("Loss test recorded");
                        await refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Loss test failed");
                      }
                    }} />
            );
          })}
          {/* Side states: paused + fault — always reachable. */}
          <SideStates fsm={fsm} busy={busy} onAdvance={advance} />
        </ol>

        {/* Readiness summary */}
        <ReadinessCard fsm={fsm} />
      </main>
    </div>
  );
}


function Step(props) {
  const { state, label, blurb, index, isCurrent, isPast, isNext, fsm,
          group, members, memberTypes, typeCatalog, schedSlots,
          fillTests, lossTests, psetInput, setPsetInput, busy, onAdvance,
          onAssignType, onAddSlot, onRunFillTest, onRunLossTest } = props;

  const ringClass = isCurrent ? "border-blue-600 bg-blue-50"
                  : isPast    ? "border-emerald-400 bg-emerald-50"
                              : "border-slate-200 bg-white";
  const dotIcon   = isCurrent ? <Circle size={12} weight="fill" className="text-blue-600 animate-pulse" />
                  : isPast    ? <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                              : <Circle size={10} className="text-slate-300" />;

  // iter150AG.fix2 — actions are now rendered in the row whose own
  // FSM state matches the upcoming action (i.e. `fsm.next === state`).
  // Previously we keyed actions by `fsm.next` from inside the
  // `fsm.current` row, which put the schedule editor inside the
  // "Accept auto-control disclaimer" card. Now each row's action
  // matches its label.
  const showActions = isCurrent;

  return (
    <li data-testid={`fsm-step-${state}`}
        className={`border ${ringClass} rounded-sm p-3 transition-colors`}>
      <div className="flex items-start gap-2.5">
        <div className="w-5 mt-0.5 shrink-0 flex justify-center">{dotIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-slate-400">{String(index).padStart(2, "0")}</span>
            <h3 className="text-sm font-bold font-['Chivo'] text-slate-900">{label}</h3>
            {isCurrent && (
              <span data-testid={`fsm-here-${state}`}
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">
                you are here
              </span>
            )}
            {isPast && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">
                done
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{blurb}</p>

          {showActions && (
            <div className="mt-3 space-y-2">
              {state === "type_verified" && (
                <TypeVerifiedActions members={members} memberTypes={memberTypes}
                                      typeCatalog={typeCatalog} onAssignType={onAssignType}
                                      busy={busy} />
              )}
              {state === "pset_set" && (
                <PsetSetActions group={group} psetInput={psetInput}
                                setPsetInput={setPsetInput} busy={busy}
                                onAdvance={onAdvance} />
              )}
              {state === "disclaimer1_accepted" && (
                <DisclaimerActions kind="pre_test" busy={busy}
                                    onAccept={() => onAdvance("disclaimer1_accepted",
                                                                { accept_pre_test_disclaimer: true })} />
              )}
              {state === "disclaimer2_accepted" && (
                <DisclaimerActions kind="auto_control" busy={busy}
                                    onAccept={() => onAdvance("disclaimer2_accepted",
                                                                { accept_auto_control_disclaimer: true })} />
              )}
              {state === "fill_test" && (
                <FillTestActions members={members} fillTests={fillTests}
                                  busy={busy} onRunFillTest={onRunFillTest} />
              )}
              {state === "loss_test" && (
                <LossTestActions lossTests={lossTests} busy={busy}
                                  onRunLossTest={onRunLossTest} />
              )}
              {state === "schedule_set" && (
                <ScheduleActions slots={schedSlots} busy={busy} onAddSlot={onAddSlot} />
              )}

              {/* Universal advance button for any one-step move. */}
              {isCurrent && fsm.next && (
                <button
                  data-testid={`fsm-advance-${state}`}
                  disabled={busy || (fsm.next_verdict && !fsm.next_verdict.ok)}
                  onClick={() => onAdvance(fsm.next)}
                  className="text-[11px] inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  Advance to <span className="font-mono">{fsm.next}</span>
                  <ArrowRight size={12} weight="bold" />
                </button>
              )}

              {/* Show blocker list, if any. */}
              {isCurrent && fsm.next_verdict && !fsm.next_verdict.ok
               && fsm.next_verdict.required?.length > 0 && (
                <ul data-testid={`fsm-blockers-${state}`}
                    className="text-[11px] text-rose-700 list-disc pl-5 mt-1 space-y-0.5">
                  {fsm.next_verdict.required.map((r, idx) => <li key={idx}>{r}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function TypeVerifiedActions({ members, memberTypes, typeCatalog, onAssignType, busy }) {
  if (!members?.length) return (
    <p className="text-[11px] text-amber-700">Add at least one member to the group first.</p>
  );
  const codes = (typeCatalog || []).map(t => t.type_code);
  return (
    <div data-testid="fsm-type-actions" className="space-y-1.5">
      {members.map(m => (
        <div key={m.device_id} className="flex items-center gap-2 text-xs">
          <span className="font-mono text-slate-700 w-48 truncate"
                 title={m.device_id}>
            {m.serial_number || m.device_id}
          </span>
          <select
            data-testid={`fsm-type-select-${m.device_id}`}
            value={memberTypes[m.device_id] || ""}
            disabled={busy}
            onChange={e => e.target.value && onAssignType(m.device_id, e.target.value)}
            className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white">
            <option value="">Assign type…</option>
            {codes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {memberTypes[m.device_id] && (
            <CheckCircle size={14} weight="fill" className="text-emerald-600" />
          )}
        </div>
      ))}
      {codes.length === 0 && (
        <p className="text-[11px] text-amber-700">
          No types in the catalog yet — admins can add them at <code>/sequencing/types</code>.
        </p>
      )}
    </div>
  );
}

function PsetSetActions({ group, psetInput, setPsetInput, busy, onAdvance }) {
  const v = parseFloat(psetInput);
  const valid = !Number.isNaN(v) && v >= 0.5 && v <= 25.0;
  return (
    <div data-testid="fsm-pset-actions" className="flex items-center gap-2">
      <input
        type="number" step="0.1" min="0.5" max="25"
        value={psetInput}
        onChange={e => setPsetInput(e.target.value)}
        placeholder="bar"
        data-testid="fsm-pset-input"
        className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white w-24 font-mono" />
      <button
        data-testid="fsm-pset-save"
        disabled={!valid || busy}
        onClick={() => onAdvance("pset_set", { common_pset: v })}
        className="text-[11px] px-2 py-1 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
        Save & advance
      </button>
      {!valid && psetInput && (
        <span className="text-[10px] text-rose-600">0.5–25 bar</span>
      )}
    </div>
  );
}

function DisclaimerActions({ kind, busy, onAccept }) {
  return (
    <div data-testid={`fsm-disc-${kind}`} className="bg-amber-50 border border-amber-200 rounded-sm p-2">
      <p className="text-[11px] text-amber-900 leading-relaxed">
        {kind === "pre_test"
          ? "By accepting, I confirm the area is safe, lines will be emptied when instructed, and no air will be used during test windows."
          : "By accepting, I confirm setup is correct, compressor identity is verified, and the system may start/stop compressors automatically."}
      </p>
      <button
        data-testid={`fsm-disc-${kind}-accept`}
        disabled={busy}
        onClick={onAccept}
        className="mt-2 text-[11px] px-2 py-1 rounded-sm border border-amber-700 bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-40">
        I accept
      </button>
    </div>
  );
}

function FillTestActions({ members, fillTests, busy, onRunFillTest }) {
  const tested = useMemo(() =>
    new Set((fillTests || [])
            .filter(t => t.result && t.result !== "pending")
            .map(t => t.device_id))
  , [fillTests]);
  return (
    <div data-testid="fsm-fill-actions" className="space-y-1.5">
      {(members || []).map(m => (
        <div key={m.device_id} className="flex items-center gap-2 text-xs">
          <span className="font-mono text-slate-700 w-48 truncate"
                 title={m.device_id}>
            {m.serial_number || m.device_id}
          </span>
          {tested.has(m.device_id) ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px]">
              <CheckCircle size={12} weight="fill" /> tested
            </span>
          ) : (
            <button
              data-testid={`fsm-fill-run-${m.device_id}`}
              disabled={busy}
              onClick={() => onRunFillTest(m.device_id)}
              className="text-[11px] px-2 py-0.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Record passed test
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function LossTestActions({ lossTests, busy, onRunLossTest }) {
  const done = (lossTests || []).filter(t => t.result && t.result !== "pending").length;
  return (
    <div data-testid="fsm-loss-actions" className="flex items-center gap-2 text-xs">
      <span className="text-slate-600">{done} completed</span>
      <button
        data-testid="fsm-loss-run"
        disabled={busy}
        onClick={onRunLossTest}
        className="text-[11px] px-2 py-0.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        Record loss test
      </button>
    </div>
  );
}

function ScheduleActions({ slots, busy, onAddSlot }) {
  const present = new Set((slots || []).filter(s => s.run).map(s => s.weekday));
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div data-testid="fsm-schedule-actions" className="flex items-center gap-1.5 flex-wrap">
      {days.map((d, i) => (
        <button
          key={d}
          data-testid={`fsm-sched-${i}`}
          disabled={busy || present.has(i)}
          onClick={() => onAddSlot(i)}
          className={`text-[11px] px-2 py-0.5 rounded-sm border ${
            present.has(i)
              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}
            disabled:cursor-default`}>
          {d}{present.has(i) ? " ✓" : ""}
        </button>
      ))}
    </div>
  );
}

function SideStates({ fsm, busy, onAdvance }) {
  if (!["auto_sequencing", "paused", "fault"].includes(fsm.current)
      && fsm.current_index < 5) return null;
  return (
    <li className="border border-dashed border-slate-300 rounded-sm p-3 mt-3"
        data-testid="fsm-side-states">
      <div className="flex items-center gap-2 flex-wrap">
        <Lock size={14} className="text-slate-400" />
        <span className="text-xs font-bold text-slate-700">Side states</span>
        {fsm.current !== "paused" && (
          <button
            data-testid="fsm-pause"
            disabled={busy}
            onClick={() => onAdvance("paused")}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-40">
            <Pause size={12} /> Pause
          </button>
        )}
        {fsm.current === "paused" && (
          <button
            data-testid="fsm-resume"
            disabled={busy}
            onClick={() => onAdvance("ready_to_start")}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
            Resume to ready
          </button>
        )}
        {fsm.current !== "fault" && (
          <button
            data-testid="fsm-mark-fault"
            disabled={busy}
            onClick={() => onAdvance("fault")}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-40">
            <Warning size={12} /> Mark fault
          </button>
        )}
      </div>
    </li>
  );
}

function ReadinessCard({ fsm }) {
  const r = fsm.ready_verdict;
  const t = fsm.totals;
  return (
    <section data-testid="fsm-readiness"
             className={`mt-4 border rounded-sm p-3 ${
               r?.ok ? "border-emerald-300 bg-emerald-50"
                     : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        <Compass size={14} className={r?.ok ? "text-emerald-700" : "text-slate-500"} />
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
          Readiness for auto-sequencing
        </h2>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
          r?.ok ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
          {r?.ok ? "READY" : "NOT READY"}
        </span>
      </div>
      <ul className="text-[11px] text-slate-600 mt-2 grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-3">
        <li>Members: <b>{t.members}</b></li>
        <li>Types assigned: <b>{t.types_assigned}</b></li>
        <li>Pset: <b>{t.common_pset ?? "—"}</b></li>
        <li>Fill tests: <b>{t.fill_tests_completed}</b></li>
        <li>Loss tests: <b>{t.loss_tests_completed}</b></li>
        <li>Stop windows: <b>{t.schedule_slots}</b></li>
        <li>Pre-test accepts: <b>{t.disclaimer_pre_test}</b></li>
        <li>Auto-control accepts: <b>{t.disclaimer_auto_ctrl}</b></li>
      </ul>
      {r && !r.ok && r.required?.length > 0 && (
        <ul className="text-[11px] text-rose-700 list-disc pl-5 mt-2 space-y-0.5"
            data-testid="fsm-readiness-blockers">
          {r.required.map((line, idx) => <li key={idx}>{line}</li>)}
        </ul>
      )}
    </section>
  );
}
